import type { FinancialPropertyListItem } from "@/features/financialShared";
import {
	ServicePeriodStatus,
	UnitPayoutStatus,
} from "@/features/financialShared";
import type {
	RevenueUnitDetail,
	RevenueUnitListItem,
} from "./revenueManagement.types";

export const REVENUE_PROPERTIES: FinancialPropertyListItem[] = [
	{
		id: "prop-oo-mandarina",
		name: "One&Only Mandarina",
		totalUnits: 48,
		latestServicePeriodLabel: "Jan 15, 2026 – Mar 15, 2026",
		status: ServicePeriodStatus.PUBLISHED,
		lastUpdatedDate: "Feb 15, 2026",
		approver: "Sarah Johnson",
	},
	{
		id: "prop-siro-boka",
		name: "SIRO Boka Place",
		totalUnits: 32,
		latestServicePeriodLabel: "Oct 1, 2025 – Dec 31, 2025",
		status: ServicePeriodStatus.COMPLETED,
		lastUpdatedDate: "Feb 12, 2026",
		approver: "Michael Chen",
	},
	{
		id: "prop-atlantis",
		name: "Atlantis The Royal",
		totalUnits: 52,
		latestServicePeriodLabel: "Jan 1, 2026 – Mar 31, 2026",
		status: ServicePeriodStatus.DRAFT,
		lastUpdatedDate: "Feb 8, 2026",
		approver: "Sarah Johnson",
	},
	{
		id: "prop-four-seasons",
		name: "Four Seasons Resort Dubai",
		totalUnits: 24,
		latestServicePeriodLabel: "Apr 1, 2026 – Jun 30, 2026",
		status: ServicePeriodStatus.PUBLISHED,
		lastUpdatedDate: "Feb 5, 2026",
		approver: "Ayush Mehta",
	},
];

const REVENUE_UNITS: RevenueUnitListItem[] = [
	{
		id: "unit-v50",
		unitCode: "V-50",
		ownerName: "Mr Zahir Moossa",
		ownerEmail: "zahir.moossa@example.com",
		nights: 42,
		netRevenue: 862192,
		ownerShare: 431096,
		balanceToPay: null,
		payoutMethod: "Bank Transfer",
		status: UnitPayoutStatus.SETTLED,
		isPaid: true,
	},
	{
		id: "unit-v51",
		unitCode: "V-51",
		ownerName: "Olivia Martinez",
		ownerEmail: "olivia.martinez@example.com",
		nights: 28,
		netRevenue: 520000,
		ownerShare: 260000,
		balanceToPay: 45000,
		payoutMethod: "Cheque",
		status: UnitPayoutStatus.IN_PROGRESS,
	},
	{
		id: "unit-v52",
		unitCode: "V-52",
		ownerName: "James Wilson",
		ownerEmail: "james.wilson@example.com",
		nights: 15,
		netRevenue: 310000,
		ownerShare: 155000,
		balanceToPay: 155000,
		payoutMethod: "Bank Transfer",
		status: UnitPayoutStatus.HOLD,
	},
	{
		id: "unit-v53",
		unitCode: "V-53",
		ownerName: "Aisha Khan",
		ownerEmail: "aisha.khan@example.com",
		nights: 35,
		netRevenue: 680000,
		ownerShare: 340000,
		balanceToPay: null,
		payoutMethod: "Bank Transfer",
		status: UnitPayoutStatus.SETTLED,
		isPaid: true,
	},
];

const REVENUE_UNIT_DETAILS: Record<string, RevenueUnitDetail> = {
	"unit-v50": {
		id: "unit-v50",
		propertyId: "prop-oo-mandarina",
		periodId: "period-oct-dec-25",
		statementId: "RS-1001",
		unitCode: "V-50",
		ownerName: "Mr Zahir Moossa",
		ownerInitials: "ZM",
		ownerPhone: "+971 50 123 4567",
		ownerEmail: "zahir.moossa@example.com",
		unitDetail: "One&Only Mandarina · V-50 · 3B",
		status: UnitPayoutStatus.SETTLED,
		servicePeriodLabel: "Oct 25, 2025 – Dec 25, 2025",
		statementDate: "Feb 10, 2026",
		payoutDate: "Feb 5, 2026",
		totalAmountToTransfer: 181464,
		rentalActivityRows: [
			{
				id: "ra-1",
				period: "Oct 2025",
				unit: "V-50",
				type: "Rental",
				arrival: "Oct 5, 2025",
				departure: "Oct 18, 2025",
				resvNights: 13,
				netRoomRevenue: 431096,
			},
			{
				id: "ra-2",
				period: "Nov 2025",
				unit: "V-50",
				type: "Rental",
				arrival: "—",
				departure: "—",
				resvNights: "—",
				netRoomRevenue: "—",
			},
			{
				id: "ra-3",
				period: "Dec 2025",
				unit: "V-50",
				type: "Rental",
				arrival: "—",
				departure: "—",
				resvNights: "—",
				netRoomRevenue: "—",
			},
		],
		rentalSummaryRows: [
			{
				id: "rs-1",
				lineItem: "Rental Nights",
				oct: 13,
				nov: "—",
				dec: "—",
				total: 13,
			},
			{
				id: "rs-2",
				lineItem: "Gross Room Rental Revenue",
				oct: 500000,
				nov: "—",
				dec: "—",
				total: 500000,
			},
			{
				id: "rs-3",
				lineItem: "(-) VAT 15%",
				oct: -75000,
				nov: "—",
				dec: "—",
				total: -75000,
				isDeduction: true,
			},
			{
				id: "rs-4",
				lineItem: "(-) Environment Protection Fee 0.85%",
				oct: -4250,
				nov: "—",
				dec: "—",
				total: -4250,
				isDeduction: true,
			},
			{
				id: "rs-5",
				lineItem: "Net Room Rental Revenue",
				oct: 431096,
				nov: "—",
				dec: "—",
				total: 431096,
				isSubtotal: true,
			},
			{
				id: "rs-6",
				lineItem: "Total Amount to Transfer",
				oct: 181464,
				nov: "—",
				dec: "—",
				total: 181464,
				isGrandTotal: true,
			},
		],
	},
};

export function getRevenueProperties(): FinancialPropertyListItem[] {
	return [...REVENUE_PROPERTIES];
}

export function getRevenuePropertyById(
	id: string,
): FinancialPropertyListItem | undefined {
	return REVENUE_PROPERTIES.find((property) => property.id === id);
}

export function getRevenueUnitsByPeriod(
	_periodId: string,
): RevenueUnitListItem[] {
	return [...REVENUE_UNITS];
}

export function getRevenueUnitDetail(
	unitId: string,
): RevenueUnitDetail | undefined {
	return REVENUE_UNIT_DETAILS[unitId];
}

export function getRevenuePropertyOptionsForModal(): {
	value: string;
	label: string;
}[] {
	return REVENUE_PROPERTIES.map((property) => ({
		value: property.id,
		label: property.name,
	}));
}
